import { pitchToY } from "./pitchMath.js";
import { ledgerLineGeometry } from "./ledgerLineGeometry.js";
import { ACCIDENTAL_INK_PROFILES } from "./accidentalInkProfiles.js";

// Ink bounds after the transforms used by NoteGlyph, relative to its
// accidentalOffsetX and notehead origin. Double accidentals are wider.
const INK = {
  flat: [2.19, -13.95, 9, 4.89],
  sharp: [2, -11.25, 10.25, 11.25],
  natural: [3.76, -11.46, 9.22, 11.46],
  doubleFlat: [-7.938, -17.008, 9.123, 5.647],
  doubleSharp: [-0.304, -5.067, 9.416, 4.527]
};

function accidentalKind(accidental) {
  return ({ b: "flat", "♭": "flat", "#": "sharp", "♯": "sharp",
    n: "natural", "♮": "natural", bb: "doubleFlat", ff: "doubleFlat", "𝄫": "doubleFlat",
    "##": "doubleSharp", x: "doubleSharp", "𝄪": "doubleSharp" })[accidental];
}

export function accidentalInkBounds(accidental) {
  return INK[accidentalKind(accidental)] || null;
}

export function accidentalRightInBand(accidental, low, high) {
  const profile = ACCIDENTAL_INK_PROFILES[accidentalKind(accidental)];
  if (!profile) return null;
  let right = null;
  for (let i = 0; i < profile.right.length; i++) {
    const top = profile.start + i * profile.step;
    if (top > high || top + profile.step < low) continue;
    const ink = profile.right[i];
    if (ink != null) right = right == null ? ink : Math.max(right, ink);
  }
  return right;
}

// All offsets are computed in a shared chord coordinate system first,
// then translated back into each note's local Glyph coordinate system.
export function arrangeChordAccidentals(notes, clef) {
  const gap = 3;
  const items = notes.map(note => {
    const bounds = accidentalInkBounds(note.accidental);
    const y = pitchToY(note.pitch, clef);
    return bounds && { note, bounds, top: y + bounds[1], bottom: y + bounds[3], y };
  }).filter(Boolean).sort((a, b) => a.y - b.y || a.note.id.localeCompare(b.note.id));
  if (!items.length) return;

  // Displaced noteheads affect the whole bank. Ledger lines do not:
  // test their painted segments against the accidental's local ink below.
  const headLeft = Math.min(...notes.map(note => note.renderX || 0));
  const ledgerLines = notes.flatMap(note =>
    ledgerLineGeometry(note.pitch, clef, note.symbol || note.entry?.symbol || "whole_note")
      .map(line => ({
        left: (note.renderX || 0) + line.x1,
        y: pitchToY(note.pitch, clef) + line.y
      })));

  const columns = [];
  for (const item of items) {
    let index = 0;
    while (columns[index]?.some(other =>
      item.top < other.bottom + 2 && other.top < item.bottom + 2)) index++;
    (columns[index] ||= []).push(item);
  }

  let right = headLeft - gap;
  for (const column of columns) {
    for (const item of column) {
      for (const line of ledgerLines) {
        // The segment is one pixel thick. Inspect only the strip of the
        // glyph at that height, with a small clearance around the stroke.
        const inkRight = accidentalRightInBand(
          item.note.accidental, line.y - item.y - 0.75, line.y - item.y + 0.75);
        if (inkRight == null) continue;
        const candidateInkRight = right - item.bounds[2] + inkRight;
        if (candidateInkRight > line.left - 1.5) {
          right = line.left - 1.5 - inkRight + item.bounds[2];
        }
      }
    }
    let width = 0;
    for (const { note, bounds } of column) {
      note.accidentalOffsetX = right - bounds[2] - (note.renderX || 0);
      width = Math.max(width, bounds[2] - bounds[0]);
    }
    right -= width + gap;
  }
}
